import { useAppSelector } from "../../../store/hooks";
import {
  useGetCurrentSeasonQuery,
  useGetSeasonRosterQuery,
  useGetSeasonStandingsQuery,
} from "../api/gameApi";
import type { GameFrames, Roster, Season, Standings } from "../api/types";

export interface CurrentGame {
  season: Season | null;
  standings: Standings | undefined;
  roster: Roster | undefined;
  frames: GameFrames | null;
  /** Something failed: callers show nothing rather than an error. */
  failed: boolean;
}

/**
 * The current season with its standings (active, ended) or roster (before it
 * starts), for the boards outside /game. Shares GamePage's cache entries, so it
 * waits for the auth bootstrap like GamePage does: a request sent before the
 * token is ready would cache a season without `me` for both.
 */
export function useCurrentGame(): CurrentGame {
  const ready = useAppSelector((s) => s.auth.bootstrapStatus) === "ready";
  const { data: season, isError } = useGetCurrentSeasonQuery(undefined, { skip: !ready });
  const id = season?.id ?? "";
  const beforeStart = season?.phase === "announced" || season?.phase === "open";
  const { data: standings, isError: standingsFailed } = useGetSeasonStandingsQuery(id, {
    skip: !id || beforeStart,
  });
  const { data: roster, isError: rosterFailed } = useGetSeasonRosterQuery(id, { skip: !id || !beforeStart });
  return {
    season: season ?? null,
    standings,
    roster,
    frames: standings?.frames ?? null,
    failed: isError || (beforeStart ? rosterFailed : standingsFailed),
  };
}
